import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { AuditLogFromCell, AuditLogToCell } from '../AuditLogTableCells';
import { formatDateTime } from '../../utils/dateFormat';
import { ORDER_STATUSES, ORDER_GROUP_STATUSES, DIAMOND_TYPES } from '../../constants/appConstants';

export function getAuditStatusLabelCashiering(status) {
  if (!status) return '';
  const labels = {
    requested: 'Requested',
    approved: 'Approved',
    ready: 'Ready',
    canceled: 'Cancelled',
    cancelled: 'Cancelled',
    expired: 'Expired',
    memo: 'Memo',
    paid: 'Paid',
    open: 'Open',
  };
  return labels[status] || status;
}

export function getAuditStatusLabelClientDetail(status) {
  if (!status) return '';
  const allLabels = {
    [ORDER_STATUSES.REQUESTED]: 'Requested',
    [ORDER_STATUSES.APPROVED]: 'Approved',
    [ORDER_STATUSES.READY]: 'Ready',
    [ORDER_STATUSES.CANCELED]: 'Cancelled',
    [ORDER_STATUSES.EXPIRED]: 'Expired',
    [ORDER_STATUSES.MEMO]: 'Memo',
    [ORDER_STATUSES.PAID]: 'Paid',
    [ORDER_GROUP_STATUSES.OPEN]: 'Open',
    [ORDER_GROUP_STATUSES.READY]: 'Ready',
    [ORDER_GROUP_STATUSES.MEMO]: 'Memo',
    [ORDER_GROUP_STATUSES.PAID]: 'Paid',
    [ORDER_GROUP_STATUSES.CANCELLED]: 'Cancelled',
  };
  return allLabels[status] || status;
}

export function formatAuditAction(action) {
  const labels = {
    order_created: 'Order Created',
    order_status_change: 'Order Status Change',
    item_status_change: 'Item Status Change',
    item_returned_to_inventory: 'Returned to Inventory',
    item_price_updated: 'Price/Weight Updated',
    item_client_changed: 'Client Changed',
    check_discount_updated: 'Discount Updated',
    check_deleted: 'Check Deleted',
  };
  return labels[action] || action;
}

export function getAuditActionColor(action) {
  switch (action) {
    case 'order_created': return 'primary';
    case 'order_status_change': return 'warning';
    case 'item_status_change': return 'info';
    case 'item_returned_to_inventory': return 'success';
    case 'item_price_updated': return 'info';
    default: return 'default';
  }
}

export function formatAuditDetails(log) {
  const parts = [];
  if (log.details?.itemType) {
    parts.push(log.details.itemType === DIAMOND_TYPES.NON_CERTIFIED ? 'Non-certified' : log.details.itemType === DIAMOND_TYPES.CUSTOM ? 'Custom' : 'Certified');
  }
  if (log.details?.returnedToInventory) parts.push('Returned to inventory');
  if (log.details?.itemCount != null) parts.push(`${log.details.itemCount} items`);
  if (log.details?.totalPrice != null) parts.push(`$${Number(log.details.totalPrice).toLocaleString()}`);
  if (log.details?.finalPrice != null) parts.push(`$${Number(log.details.finalPrice).toLocaleString()}`);
  if (log.details?.previousPricePerCarat != null || log.details?.newPricePerCarat != null) {
    const from = log.details.previousPricePerCarat != null ? `$${Number(log.details.previousPricePerCarat).toLocaleString()}` : '—';
    const to = log.details.newPricePerCarat != null ? `$${Number(log.details.newPricePerCarat).toLocaleString()}` : '—';
    parts.push(`Price/ct: ${from} → ${to}`);
  }
  if (log.details?.previousReservedCarat != null || log.details?.newReservedCarat != null) {
    const from = log.details.previousReservedCarat != null ? `${log.details.previousReservedCarat}` : '—';
    const to = log.details.newReservedCarat != null ? `${log.details.newReservedCarat}` : '—';
    parts.push(`Weight: ${from} → ${to} ct`);
  }
  if (log.details?.fromClientName && log.details?.toClientName) {
    parts.push(`${log.details.fromClientName} → ${log.details.toClientName}`);
  }
  return parts.join(' | ') || '—';
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {boolean} props.loading
 * @param {Array} props.logs
 * @param {string} props.itemRef
 * @param {boolean} props.isMobile
 * @param {function} props.getAuditStatusLabel
 * @param {function} props.getItemStatusColor
 * @param {string} props.title
 * @param {string} props.closeLabel
 * @param {string} props.emptyMessage
 * @param {'text'|'spinner'} props.loadingMode
 * @param {string} [props.loadingText]
 * @param {boolean} props.mobileCardLayout
 * @param {function} [props.t] — when provided with tableHeaderMode 'cashiering', uses i18n column headers
 * @param {'cashiering'|'plain'} [props.tableHeaderMode='plain']
 */
function ItemAuditLogDialog({
  open,
  onClose,
  loading,
  logs,
  itemRef,
  isMobile,
  getAuditStatusLabel,
  getItemStatusColor,
  title,
  closeLabel,
  emptyMessage,
  loadingMode,
  loadingText,
  mobileCardLayout,
  t,
  tableHeaderMode = 'plain',
}) {
  const headers = tableHeaderMode === 'cashiering' && t
    ? {
      date: t('cashiering.date'),
      action: t('common.actions'),
      performedBy: t('cashiering.performedBy'),
      from: t('common.from'),
      to: t('common.to'),
      details: t('common.details'),
    }
    : {
      date: 'Date',
      action: 'Action',
      performedBy: 'Performed By',
      from: 'From',
      to: 'To',
      details: 'Details',
    };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <HistoryIcon color="action" />
          <Typography variant="h6">{title}</Typography>
          {itemRef && <Chip label={itemRef} size="small" variant="outlined" sx={tableHeaderMode === 'cashiering' ? undefined : { ml: 1 }} />}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 200 }}>
        {loading ? (
          loadingMode === 'text' ? (
            <Typography sx={{ py: 3 }}>{loadingText}</Typography>
          ) : (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
          )
        ) : logs.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3 }} textAlign="center">{emptyMessage}</Typography>
        ) : mobileCardLayout && isMobile ? (
          <Stack spacing={1.5}>
            {logs.map((log) => (
              <Card key={log._id} variant="outlined">
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                    <Chip label={formatAuditAction(log.action)} size="small" color={getAuditActionColor(log.action)} variant="outlined" />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {log.createdAt ? formatDateTime(log.createdAt) : '—'}
                    </Typography>
                  </Box>
                  {log.performedBy && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      By: {`${log.performedBy.firstName || ''} ${log.performedBy.lastName || ''}`.trim() || log.performedBy.email}
                    </Typography>
                  )}
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" color="text.secondary">From:</Typography>
                      <AuditLogFromCell log={log} getAuditStatusLabel={getAuditStatusLabel} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">→</Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" color="text.secondary">To:</Typography>
                      <AuditLogToCell log={log} getAuditStatusLabel={getAuditStatusLabel} getToChipColor={getItemStatusColor} />
                    </Box>
                  </Box>
                  {formatAuditDetails(log) && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {formatAuditDetails(log)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{headers.date}</TableCell>
                  <TableCell>{headers.action}</TableCell>
                  <TableCell>{headers.performedBy}</TableCell>
                  <TableCell>{headers.from}</TableCell>
                  <TableCell>{headers.to}</TableCell>
                  <TableCell>{headers.details}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log._id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.createdAt ? formatDateTime(log.createdAt) : '—'}</TableCell>
                    <TableCell><Chip label={formatAuditAction(log.action)} size="small" color={getAuditActionColor(log.action)} variant="outlined" /></TableCell>
                    <TableCell>{log.performedBy ? `${log.performedBy.firstName || ''} ${log.performedBy.lastName || ''}`.trim() || log.performedBy.email : '—'}</TableCell>
                    <TableCell><AuditLogFromCell log={log} getAuditStatusLabel={getAuditStatusLabel} /></TableCell>
                    <TableCell><AuditLogToCell log={log} getAuditStatusLabel={getAuditStatusLabel} getToChipColor={getItemStatusColor} /></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{formatAuditDetails(log)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{closeLabel}</Button></DialogActions>
    </Dialog>
  );
}

export default ItemAuditLogDialog;
